/**
 * Below-the-fold blur, plus the doorway / article-grid reveal hooks.
 *
 * One gradual-blur is fixed to the bottom of the viewport on every page,
 * using the homepage hero settings. It stays on while content remains
 * below the fold, and fades out as the footer reaches the bottom so the
 * footer, cookie banner, and dialogs are not covered.
 *
 * One observer still marks doorway and article-grid units with
 * .section-reveal / .is-revealed. Header, footer, navigation, cookie
 * banners, dialogs, and [section-reveal-skip] sections are left alone.
 * Generic sections stay visible; the blur is the shared effect.
 */
(() => {
  const root = document.documentElement;
  if (!root.hasAttribute('data-section-transitions')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  if (reducedMotion || designMode) {
    root.setAttribute('data-section-transitions', 'reduced');
    return;
  }

  mountFoldBlur();

  function mountFoldBlur() {
    if (customElements.get('gradual-blur') == null) return;
    if (document.querySelector('[data-page-fold-blur]')) return;

    const blur = document.createElement('gradual-blur');
    blur.setAttribute('data-page-fold-blur', '');
    blur.setAttribute('target', 'page');
    blur.setAttribute('position', 'bottom');
    blur.setAttribute('height', '9rem');
    blur.setAttribute('strength', '2');
    blur.setAttribute('div-count', '5');
    blur.setAttribute('curve', 'bezier');
    blur.setAttribute('exponential', 'true');
    blur.setAttribute('opacity', '1');
    blur.setAttribute('z-index', '80');
    blur.setAttribute('aria-hidden', 'true');
    document.body.appendChild(blur);
    blur.classList.add('page-fold-blur');
    blur.style.transition = 'none';

    const footer = document.querySelector('.site-footer');
    const bannerSelector = '#shopify-pc__banner, .shopify-pc__banner, #shopify-privacy-banner';
    let ticking = false;

    const isVisible = (el) => {
      if (!(el instanceof Element)) return false;
      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (Number(style.opacity) === 0) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const apply = () => {
      ticking = false;

      if (isVisible(document.querySelector(bannerSelector)) || document.querySelector('dialog[open]')) {
        blur.style.opacity = '0';
        return;
      }

      const viewH = window.innerHeight || root.clientHeight;
      const remaining = root.scrollHeight - window.scrollY - viewH;
      if (remaining <= 8) {
        blur.style.opacity = '0';
        return;
      }

      let opacity = 1;
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const band = blur.getBoundingClientRect().height || 144;
        if (footerTop < viewH) {
          opacity = Math.min(1, Math.max(0, (footerTop - (viewH - band)) / band));
        }
      }

      blur.style.opacity = String(opacity);
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    new MutationObserver(request).observe(document.body, { childList: true });
  }

  const main = document.getElementById('main');
  if (!main || !('IntersectionObserver' in window)) return;

  const SKIP = '.section-reveal-skip, .page-background-section';
  const BANNER = '#shopify-pc__banner, .shopify-pc__banner, #shopify-privacy-banner';
  const CHROME = `header, footer, dialog, [role="dialog"], ${SKIP}, ${BANNER}`;
  const armed = new WeakSet();

  let observer = null;

  const viewLimit = () => (window.innerHeight || root.clientHeight) * 0.98;

  const isBelowFold = (el) => {
    const rect = el.getBoundingClientRect();
    if (rect.height === 0 && rect.width === 0) return false;
    return rect.top >= viewLimit();
  };

  const finish = (el) => {
    el.classList.add('section-reveal', 'is-revealed');
    if (observer) observer.unobserve(el);
  };

  const snap = (el) => {
    const suppress = root.classList.contains('section-transitions-ready');
    if (suppress) el.classList.add('section-reveal-hold');
    finish(el);
    if (suppress) {
      void el.offsetWidth;
      el.classList.remove('section-reveal-hold');
    }
  };

  const reveal = (el) => {
    finish(el);
  };

  const skipSection = (section) => {
    if (!(section instanceof Element)) return true;
    if (section.matches(SKIP) || section.closest(SKIP)) return true;
    if (section.querySelector('[data-section-reveal], [data-blog-case-study]')) return true;
    if (section.querySelector(BANNER)) return true;
    return false;
  };

  const collect = (scope) => {
    const found = [];
    const seen = new Set();

    const add = (el) => {
      if (!(el instanceof Element) || seen.has(el)) return;
      if (el.closest(CHROME)) return;
      if (!main.contains(el)) return;
      seen.add(el);
      found.push(el);
    };

    const context = scope && scope.querySelectorAll ? scope : document;

    context.querySelectorAll('[data-section-reveal]').forEach(add);

    const sections = [];
    if (context === document || context === main) {
      main.querySelectorAll(':scope > .shopify-section').forEach((section) => sections.push(section));
    } else if (context.matches?.('.shopify-section') && context.parentElement === main) {
      sections.push(context);
    } else {
      context.querySelectorAll?.('.shopify-section')?.forEach((section) => {
        if (section.parentElement === main) sections.push(section);
      });
    }

    sections.forEach((section) => {
      if (skipSection(section)) return;
      add(section);
    });

    return found;
  };

  const arm = (el) => {
    if (el.classList.contains('is-revealed')) {
      armed.add(el);
      return;
    }
    if (armed.has(el) && el.classList.contains('section-reveal')) return;
    armed.add(el);

    if (!isBelowFold(el)) {
      snap(el);
      return;
    }

    const suppress = root.classList.contains('section-transitions-ready');
    if (suppress) el.classList.add('section-reveal-hold');
    el.classList.add('section-reveal');
    if (suppress) {
      void el.offsetWidth;
      el.classList.remove('section-reveal-hold');
    }

    observer.observe(el);
  };

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -2% 0px',
      // Threshold 0 so a tall section reveals as soon as it enters.
      // A fractional threshold would wait for a large slice of that
      // section and leave its top invisible.
      threshold: 0,
    }
  );

  collect(document).forEach(arm);

  // Commit the hidden state before transitions exist, so below-the-fold
  // sections do not fade out on load and above-the-fold sections do not
  // fade in.
  void root.offsetWidth;
  root.classList.add('section-transitions-ready');

  // Scroll restoration and hash jumps can move sections into view after
  // the first measurement. Show those immediately.
  requestAnimationFrame(() => {
    document.querySelectorAll('.section-reveal:not(.is-revealed)').forEach((el) => {
      if (!isBelowFold(el)) snap(el);
    });
  });

  if (location.hash.length > 1) {
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    } catch {
      target = null;
    }
    const host = target?.closest?.('.section-reveal, [data-section-reveal]');
    if (host) snap(host);
  }

  document.addEventListener('focusin', (event) => {
    const el = event.target?.closest?.('.section-reveal');
    if (!el || el.classList.contains('is-revealed')) return;
    reveal(el);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    if (window.Shopify && window.Shopify.designMode) {
      section.querySelectorAll('.section-reveal').forEach(reveal);
      if (section.classList.contains('section-reveal')) reveal(section);
      return;
    }
    collect(section).forEach(arm);
  });

  const mutations = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (
          node.matches('.shopify-section, [data-section-reveal]') ||
          node.querySelector('[data-section-reveal], .shopify-section')
        ) {
          collect(node).forEach(arm);
        }
      });
    });
  });

  mutations.observe(main, { childList: true });
})();
